// Crash reporting wiring (WebView layer).
//
// Picks the crash-reporter backend and defers init until consent first
// becomes true. Import this from `main.tsx` once at startup; it does NOT
// init at import time — it subscribes to the consent observable and only
// initializes the SDK once consent flips true (fixing the earlier
// import-time race where init ran before the persisted consent flag was
// hydrated).
//
// The vendor lives behind `CrashReporter` (see `lib/crash/`), mirroring
// the Rust `songbird_analytics::crash::CrashReporter` trait — swapping
// vendors is a new impl plus the one line below that constructs it.
//
// `scrubUrl` is re-exported for tests and any breadcrumb-scrubbing caller.

import { onAnalyticsConsent } from '@/lib/consent';
import type { CrashReporter } from '@/lib/crash/reporter';
import { SentryReporter } from '@/lib/crash/sentryReporter';

export { scrubUrl } from '@/lib/crash/reporter';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// The active crash-reporting backend. One line to swap vendors.
const reporter: CrashReporter = new SentryReporter();

// Defer everything to consent. The observable fires immediately with the
// current value, so a returning consented user inits on the first tick
// (once settingsStore hydration has run); everyone else stays inert until
// they opt in via the Privacy toggle.
onAnalyticsConsent((granted) => {
  reporter.setConsent(granted);
  if (granted && dsn) {
    reporter.init({
      dsn,
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
      environment: import.meta.env.DEV ? 'development' : 'production',
      // `||` (not `??`) so an empty-string env value falls back to 0.1.
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.1),
    });
  }
});

export {};
